import React, { type ChangeEvent, type FocusEvent } from 'react';
import { styled } from 'styled-components';
import CheckboxGroup from '@ingka/checkbox-group';
import TextArea from '@ingka/text-area';
import { Select, Option } from '@ingka/select';
import { FileUploadArea } from '@ikea-customer-self-serve/contact-form';
import { mediaQueries } from '../styles/mediaQueries';
import {
  type Choice,
  type FileAnswer,
  type Question,
} from '../services/additionalInfo/model';
import colors from '../styles/colors';

/* globals CSS_CONFIG */

const SelectWrapper = styled.div`
  margin-top: 1rem;

  &&& {
    .select__wrapper > select {
      height: 48px;
    }
  }
`;

const StyledCheckboxGroup = styled(CheckboxGroup)`
  &&& {
    padding-top: 0.5rem;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    column-gap: 0.5rem;

    ${mediaQueries.landscapePhoneUp} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${mediaQueries.desktopUp} {
      grid-template-columns: repeat(3, 1fr);
    }

    & > * {
      word-break: break-word;
      overflow-wrap: break-word;
    }

    .checkbox__label {
      label {
        font-weight: 400;
      }
    }

    legend {
      font-weight: 700;
      margin-bottom: 0.5rem;
      font-size: 14px;
      color: ${colors.text.default};
    }
  }
`;

const StyledSelect = styled(Select)`
  &&& {
    .select__wrapper > select {
      height: 48px;
    }
  }
`;

interface QuestionsListItemProps {
  question: Question;
  onChangeInputHandler: (
    e: ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    > | null,
    files?: FileAnswer[],
  ) => void;
  answer: string[] | FileAnswer[];
  onBlur?: () => void;
  disabled?: boolean;
}

const { language } = CSS_CONFIG;

const QuestionsListItem: React.FC<QuestionsListItemProps> = ({
  question,
  onChangeInputHandler,
  answer,
  onBlur,
  disabled = false,
}) => {
  const { id: questionId, options, text, type: questionType } = question;
  const questionText = text[language];
  const isTextItem = questionType === 'TEXT';
  const isCheckBoxItem = questionType === 'CHECKBOX';
  const isSingleChoiceItem = questionType === 'RADIO';
  const isFileUploadItem = questionType === 'FILE';

  const onFileChangeHandler = (files: FileAnswer[]) => {
    onChangeInputHandler(null, files);
  };

  const onBlurHandler = (e?: FocusEvent<HTMLElement>) => {
    if (isCheckBoxItem && e?.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    onBlur?.();
  };

  const checkboxArray = isCheckBoxItem
    ? options.map((option: Choice) => {
        return {
          id: option.id.toString(10),
          label: option.text[language],
          text: option.text[language],
          value: option.id.toString(10),
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            onChangeInputHandler(e),
          checked: (answer as string[]).includes(`${option.id}`),
          disabled,
        };
      })
    : [];

  return (
    <>
      {isTextItem && (
        <TextArea
          id={`${questionId}`}
          value={answer.length > 0 ? (answer[0] as string) : ''}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            onChangeInputHandler(e)
          }
          onBlur={onBlurHandler}
          label={questionText}
          data-cy="text-area"
          disabled={disabled}
          required
          data-cs-mask
        />
      )}
      {isCheckBoxItem && (
        <StyledCheckboxGroup
          id={`questionCheckboxGroup-${questionId}`}
          key={`replies${JSON.stringify(answer)}`}
          name={questionText}
          list={checkboxArray}
          data-cy="checkbox-group"
          required
          onBlur={onBlurHandler}
        />
      )}

      {isSingleChoiceItem && (
        <SelectWrapper>
          <StyledSelect
            hintText=""
            id={`singleChoiceQuestionSelect-${questionId}`}
            label={questionText}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              onChangeInputHandler(e)
            }
            onBlur={onBlurHandler}
            data-cy="single-choice-question-select"
            disabled={disabled}
            required
          >
            <Option value="" hidden />
            {options.map((option) => (
              <Option
                key={option.id}
                value={option.id.toString(10)}
                name={option.text[language]}
              />
            ))}
          </StyledSelect>
        </SelectWrapper>
      )}
      {isFileUploadItem && (
        <FileUploadArea
          id={`file-upload-area-${questionId}`}
          label={questionText}
          data-cy="file-upload-area"
          onChange={onFileChangeHandler}
          onBlur={onBlurHandler}
          required
          disabled={disabled}
        />
      )}
    </>
  );
};

export default QuestionsListItem;
